import { FormattedMessage, useIntl } from 'react-intl'
import ConfiguratorLayout from '../../components/ConfiguratorLayout'
import { useFormikContext } from 'formik'
import { useRouter } from 'next/router'
import { MilliplexFormValues } from './Milliplex'
import { milliplexRoute, homeRoute } from '@src/routes'
import React, { useState } from 'react'
import messages from '../../messages'
import MilliplexContentComponent from './components/MilliplexContentComponent'
import NextStepButtonComponent from './components/NextStepComponent'
import { ActiveLinkType, PreviousStepLink } from './enum'
import { Button, Theme } from '@material-ui/core'
import qs from 'qs'
import { queryParamsType, getSelectedKitQueryString } from './utils'
import HandleMarkup from '@src/components/HandleMarkup'
import { makeStyles } from '@material-ui/core/styles'

const useStyles = makeStyles((theme: Theme) => {
  return {
    milliplexbottomDisclaimer: {
      paddingTop: theme.spacing(20),
      fontSize: theme.typography.pxToRem(12),
    },
  }
})

const MilliplexLayout = () => {
  const intl = useIntl()
  const classes = useStyles()
  const [displayPageError, setDisplayPageError] = useState<boolean>(false)
  const [disableSelectKitsLink, setDisableSelectKitsLink] = React.useState(true)
  const [disableReviewAndOrderLink, setDisableReviewAndOrderLink] =
    React.useState(true)
  const [disableSearchCriteriaLink, setDisableSearchCriteria] =
    useState<boolean>(false)
  const formik = useFormikContext<MilliplexFormValues>()
  const router = useRouter()
  const currentActiveLink = router.query.activeLink as string

  const constructUrl = (activeLink?: string) => {
    const params: queryParamsType = {}
    if (activeLink) {
      params.activeLink = activeLink
    }
    if (router.query.previouslyViewed) {
      params.previouslyViewed = router.query.previouslyViewed as string
    }
    if (formik.values.species) {
      params.species = formik.values.species
    }
    if (formik.values.researchAreas) {
      params.researchAreas = formik.values.researchAreas
    }
    if (formik.values.assay) {
      params.assay = formik.values.assay
    }
    if (formik.values.plateType) {
      params.plateType = formik.values.plateType
    }
    if (formik.values.instrumentType) {
      params.instrumentType = formik.values.instrumentType
    }
    if (formik.values.analytes) {
      params.analytes = formik.values.analytes
    }
    if (router.query.anyInstrument) {
      params.anyInstrument = router.query.anyInstrument as string
    }
    if (Object.values(formik.values.selectedKits).length > 0) {
      params.selectedKits = getSelectedKitQueryString(
        formik.values.selectedKits
      )
    }
    const paramString = params
      ? `?${qs.stringify(params, { arrayFormat: 'repeat', indices: false })}`
      : ''
    return `${milliplexRoute.index()}${paramString}`
  }

  const sidebarItems = [
    {
      label: intl.formatMessage(
        messages.CONF_Crispr_Search_Criteria_crisprgrna
      ),
      activeLink: currentActiveLink === undefined || currentActiveLink === '',
      url: constructUrl(),
      linkDisable: disableSearchCriteriaLink,
    },
    {
      label: intl.formatMessage(messages.PC_LEFT_NAV_ENTRY_milliplex),
      activeLink: currentActiveLink === ActiveLinkType.SelectKits,
      url: constructUrl(ActiveLinkType.SelectKits),
      linkDisable: disableSelectKitsLink,
    },
    {
      label: intl.formatMessage(messages.REVIEW_AND_ORDER),
      activeLink: currentActiveLink === ActiveLinkType.ReviewAndOrder,
      url: constructUrl(ActiveLinkType.ReviewAndOrder),
      linkDisable: disableReviewAndOrderLink,
    },
  ]

  const milliplexConfigurator = (
    <>
      <HandleMarkup value={'MILLIPLEX<sup>®</sup>'} />
      <FormattedMessage
        id="MILLIPLEX_CONFIGURATOR_TITLE"
        defaultMessage=" Configurator"
      />
    </>
  )

  const breadcrumbItems = [
    {
      url: homeRoute.index(),
      label: <FormattedMessage {...messages.CONF_HOME_CUSTOMCRISPR} />,
    },
    {
      label: milliplexConfigurator,
    },
  ]

  const handlePreviousStep = () => {
    router.push({
      query: {
        ...router.query,
        activeLink: PreviousStepLink[currentActiveLink],
      },
    })
  }

  const previousStepButtonComponent = (
    <Button
      id="milliplex-previousStep-button"
      variant="outlined"
      color="primary"
      size="large"
      onClick={handlePreviousStep}
    >
      <FormattedMessage {...messages.PREVIOUS_STEP} />
    </Button>
  )

  const previousStepButton = [ActiveLinkType.SelectKits.toString()].includes(
    currentActiveLink
  ) ? (
    previousStepButtonComponent
  ) : (
    <></>
  )

  const hasBottomDisclaimer = router.query.activeLink === 'selectKits'
  const bottomDisclaimer = (
    <div className={classes.milliplexbottomDisclaimer}>
      <HandleMarkup
        value={
          'Luminex200™, xMAP<sup>®</sup> INTELLIFLEX<sup>®</sup>, MAGPIX<sup>®</sup>, FLEXMAP 3D<sup>®</sup>, and xMAP<sup>®</sup> INTELLIFLEX<sup>®</sup> DR-SE'
        }
      />
      &nbsp;
      <FormattedMessage
        id="MILLIPLEX_STEP_TWO_BOTTOM_DISCLAIMER_PART_ONE"
        defaultMessage="are all registered trademarks of Luminex Corp."
      />
      &nbsp;
      <HandleMarkup value={'MILLIPLEX<sup>®</sup>'} />
      &nbsp;
      <FormattedMessage
        id="MILLIPLEX_STEP_TWO_BOTTOM_DISCLAIMER_PART_TWO"
        defaultMessage="is a registered trademark of Merck KGaA, Darmstadt, Germany and/or its affiliates. All Rights Reserved."
      />
    </div>
  )

  return (
    <ConfiguratorLayout
      title={milliplexConfigurator}
      sidebarItems={sidebarItems}
      breadcrumbItems={breadcrumbItems}
      bodyContent={
        <MilliplexContentComponent
          displayPageError={displayPageError}
          setDisableSelectKitsLink={setDisableSelectKitsLink}
          setDisableReviewAndOrderLink={setDisableReviewAndOrderLink}
          setDisplayPageError={setDisplayPageError}
          setDisableSearchCriteria={setDisableSearchCriteria}
        />
      }
      previousStepButton={previousStepButton}
      nextStepButton={
        <NextStepButtonComponent setDisplayPageError={setDisplayPageError} />
      }
      bottomDisclaimer={hasBottomDisclaimer ? bottomDisclaimer : undefined}
    />
  )
}

export default MilliplexLayout
